/* eslint-disable react/no-unescaped-entities */
import { useEffect, useState } from "react";
import { AlertTriangle, X } from "lucide-react";
import { api } from "../lib/api";
import { useAuth } from "../context/AuthContext";

const SESSION_KEY = "cauldron_safety_seen_session";

/**
 * SafetyWarningDialog — payment-safety popup shown to CLIENTs (seekers).
 *
 * Mounted globally in Layout, but renders only when:
 *   • The signed-in user is role CLIENT, AND
 *   • The user is on /feed, /profile/*, or just registered, AND
 *   • The user has NOT permanently acknowledged via "Don't show me again".
 *
 * Permanent dismissal is stored server-side on `users.safetyAck` so the
 * choice follows the seeker across devices.
 */
export default function SafetyWarningDialog({ trigger }) {
  // `trigger` is a string identifying the surface ("feed" | "profile" | "signup")
  // We use it only to verify the popup should appear on this surface.
  const { user } = useAuth();
  const [show, setShow] = useState(false);
  const [dontShow, setDontShow] = useState(false);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    // Only seekers see this — practitioners and admins are exempt.
    if (!user || user.role !== "CLIENT") return;
    // Within a session, only show once per page-mount cluster
    if (sessionStorage.getItem(SESSION_KEY) === "dismissed") return;

    (async () => {
      try {
        const { data } = await api.get("/me/safety-warning");
        if (data?.acknowledged) return; // permanently silenced
        setShow(true);
      } catch {
        /* noop */
      }
    })();
  }, [user, trigger]);

  if (!show) return null;

  const close = (rememberSession = true) => {
    if (rememberSession) sessionStorage.setItem(SESSION_KEY, "dismissed");
    setShow(false);
  };

  const onAcknowledge = async () => {
    setBusy(true);
    try {
      if (dontShow) {
        await api.post("/me/safety-warning", { acknowledged: true });
      }
      close(true);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-[110] bg-background/85 backdrop-blur-sm flex items-center justify-center p-4"
      data-testid="safety-warning-overlay"
    >
      <div
        className="parchment p-7 max-w-lg w-full relative space-y-4 border-2 border-destructive/60"
        data-testid="safety-warning-dialog"
      >
        <button
          onClick={() => close(true)}
          className="absolute top-3 right-3 text-foreground/50 hover:text-foreground"
          aria-label="Close"
          data-testid="safety-warning-close-btn"
        >
          <X size={18} strokeWidth={1.75} />
        </button>

        <div className="flex items-center gap-2.5">
          <div className="w-11 h-11 rounded-full bg-destructive/15 border border-destructive/40 flex items-center justify-center text-destructive">
            <AlertTriangle size={22} strokeWidth={1.75} />
          </div>
          <div>
            <div className="label-mystic text-destructive">Safety Warning</div>
            <h2 className="font-serif text-xl">Pay safely. Pay only inside Cauldron.</h2>
          </div>
        </div>

        <p className="text-sm text-foreground/85 font-semibold">
          Never send money directly to a practitioner outside of the Cauldron App.
        </p>

        <div className="text-sm text-foreground/80 space-y-2">
          <p>For your protection, all payments for spiritual services must be made through:</p>
          <ul className="list-disc pl-5 space-y-1">
            <li>The <strong>Services Tab</strong> on the practitioner's profile</li>
            <li>The <strong>Custom Payment</strong> feature also listed on the practitioner's Services Tab on their Cauldron App profile.</li>
          </ul>
          <p className="pt-1">
            Payments sent through <strong>Cash App, PayPal, Venmo, Zelle, cryptocurrency wallets, gift cards, bank transfers</strong>, or any other external method are <strong>not protected</strong> by the Cauldron App.
          </p>
          <p>
            If a practitioner requests payment outside the platform, please <a href="mailto:trust@thecauldron.app?subject=Off-Platform%20Payment%20Request" className="text-secondary underline">report them immediately</a>.
          </p>
          <p className="pt-1 text-xs text-foreground/65 italic">
            By continuing, you are acknowledging that you understand and agree to use only approved payment methods within the Cauldron App.
          </p>
        </div>

        <label
          className="flex items-center gap-2 cursor-pointer select-none pt-2"
          data-testid="safety-warning-dont-show-label"
        >
          <input
            type="checkbox"
            checked={dontShow}
            onChange={(e) => setDontShow(e.target.checked)}
            className="w-4 h-4 accent-secondary"
            data-testid="safety-warning-dont-show-checkbox"
          />
          <span className="text-xs text-foreground/75">Don't show me this message again</span>
        </label>

        <button
          type="button"
          onClick={onAcknowledge}
          disabled={busy}
          className="w-full bg-primary text-primary-foreground px-6 py-3 rounded-sm uppercase tracking-wider text-xs font-semibold disabled:opacity-50 hover:bg-primary/90 transition-colors"
          data-testid="safety-warning-ack-btn"
        >
          I understand — continue
        </button>
      </div>
    </div>
  );
}
